import React, { forwardRef, useState, useContext, useEffect } from "react";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AppContext } from "../../context/app.context";
import { ModelTsContext } from "../../context/modelTS.context";
import { ScheduleContext } from "../../context/schedule.context";
import { MILLISECONDS_PER_DAY } from "../../utility/keys";

function JobInitModal({ setOpenJobInitModal }) {
  const { weekDatesArray, dataFormatting_payrollFields, locationFilter } =
    useContext(AppContext);
  const {
    tsCreateExtraCellAttribute,
    tsRemoveExtraCellAttribute,
    projectListAfterMerge,
    projectListAfterMergeLocation,
    awsgwCandidatesResult,
    awsgwCandidatesResultLocation,
  } = useContext(ModelTsContext);
  const { setProcessingDragNDrop, dragObject, dropObject, setPendingDragUpdate } =
    useContext(ScheduleContext);

  // Use TanStack data directly (Realm EOL Jan 13, 2026)
  // Only use data if it matches current location (prevents stale data on location switch)
  const canUseTanstackProjects = projectListAfterMergeLocation === locationFilter;
  const canUseTanstackCandidates = awsgwCandidatesResultLocation === locationFilter;
  const projectsList = canUseTanstackProjects ? projectListAfterMerge : [];
  const candidatesList = canUseTanstackCandidates ? awsgwCandidatesResult : [];
  const [jobDocument, setJobDocument] = useState({});
  const [currentEstComplete, setCurrentEstComplete] = useState();
  const [currentArrivalTime, setCurrentArrivalTime] = useState();

  // console.log("JobInitModal drag: ", dragObject, ", drop: ", dropObject);

  const todayDate = new Date();
  // initial date is tomorrow
  todayDate.setDate(todayDate.getDate() + 1);

  // - - - - - - - - - -

  const onSave = () => {
    // required field validation
    if (currentArrivalTime === "") {
      window.alert("Arrival is required before Saving!");
      //
    } else {
      // push local state to Model
      jobDocument.date_estimatedCompletion = currentEstComplete;
      jobDocument.arrival_time_text = currentArrivalTime;

      // Pass the updated values to processDragNDrop
      // TanStack Query data is immutable - modifying jobDocument here doesn't
      // persist to the document that processDragNDrop finds via its own lookup
      setPendingDragUpdate({
        installer: jobDocument.installer,
        date_scheduled: jobDocument.date_scheduled,
        date_estimatedCompletion: jobDocument.date_estimatedCompletion,
        arrival_time_text: jobDocument.arrival_time_text,
        statusCode: jobDocument.statusCode,
        extraCellId: jobDocument.extraCellId,
        extraCellText: jobDocument.extraCellText,
      });

      // will trigger processDragNDrop() (scheduleContext)
      setProcessingDragNDrop(true);

      // Close modal window
      setOpenJobInitModal({ show: false });
    }
  };

  // - - - - - - - - - -

  const onEstCompleteChange = (date) => {
    const fmDate = new Date(Date.parse(date)).toLocaleDateString("en-us");
    setCurrentEstComplete(fmDate);
    console.log(
      "JobInitModal onEstCompleteChange, date: ",
      date,
      ", fmDate: ",
      fmDate
    );
  };

  // - - - - - - - - - -

  const onArrivalChange = (arrival) => {
    setCurrentArrivalTime(arrival);
    console.log("JobInitModal onArrivalChange, arrival: ", arrival);
  };

  // - - - - - - - - - -

  const EstCompleteInput = forwardRef(({ value, onClick, className }, ref) => (
    <Button
      color="clear"
      className={`${className}`}
      refInner={ref}
      onClick={onClick}
    >
      <div className="flex items-center">
        <span>{value}</span>
        <span className="ml-1 arrow arrow-down arrow-small"></span>
      </div>
    </Button>
  ));

  // - - - - - - - - - -

  useEffect(() => {
    // most of this logic is copied from ScheduleContext.processDragNDrop
    // find dropped document in ModelRwContext.projectsList
    const foundIndex = projectsList.findIndex((currentValue, index, arr) => {
      return currentValue.id === dragObject;
    });
    if (foundIndex !== -1) {
      var dayDifference = 0;
      var nonCompliantFlag = 0;
      var foundDocument = projectsList[foundIndex];
      const droppedPayrollNo = dropObject.id.slice(0, 7);
      const droppedColumnIndex = dropObject.id.slice(8);
      console.log("JobInitModal useEffect[] foundDocument: ", foundDocument);

      // 2025-05-19 - compliance JIRA SW-710 Prevent new assignment of jobs to installer if installer has a compliance hold
      const installerDocument = candidatesList.find(
        (document) =>
          dataFormatting_payrollFields(document.payrollNo) === droppedPayrollNo
      );
      nonCompliantFlag = installerDocument?.Scheduling_Hold_Flag;
      console.log(
        "JobInitModal useEffect[] Compliance installerDocument.Email: ",
        installerDocument?.Email,
        ", nonCompliantFlag: ",
        nonCompliantFlag
      );

      if (!nonCompliantFlag) {
        // unassigned jobs will likely not yet have any dates yet
        if (foundDocument.date_scheduled !== "") {
          // empty endDate not allowed - make it the same as startDate
          if (foundDocument.date_estimatedCompletion === "") {
            foundDocument.date_estimatedCompletion =
              foundDocument.date_scheduled;
          }

          // date calcs
          const timeDifference =
            new Date(foundDocument.date_estimatedCompletion).getTime() -
            new Date(foundDocument.date_scheduled).getTime();
          dayDifference = Math.round(timeDifference / (1000 * 3600 * 24));
          // console.log(
          //   "JobInitModal useEffect[] dayDifference: ",
          //   dayDifference
          // );
        }

        if (dropObject.id.slice(0, 10) === "Unassigned") {
          // if dropped onto an ExtraCell then grab the customtext & attach to job
          const extraCellId = dropObject.content?.props?.project?.id;
          const extraCellCustomText =
            dropObject.content?.props?.project?.encodedSmallTitle;
          if (extraCellCustomText && extraCellCustomText !== "") {
            // Use TanStack directly (Realm EOL Jan 13, 2026)
            tsRemoveExtraCellAttribute(extraCellId);
          }

          switch (dropObject.id) {
            case "Unassigned_Mon":
              foundDocument.date_scheduled = weekDatesArray[0];
              foundDocument.date_estimatedCompletion = new Date(
                Date.parse(weekDatesArray[0]) +
                  MILLISECONDS_PER_DAY * dayDifference
              ).toLocaleDateString("en-us");
              foundDocument.installer = "";
              foundDocument.statusCode = 4;
              foundDocument.extraCellId = extraCellId;
              foundDocument.extraCellText = extraCellCustomText;
              break;
            case "Unassigned_Tue":
              foundDocument.date_scheduled = weekDatesArray[1];
              foundDocument.date_estimatedCompletion = new Date(
                Date.parse(weekDatesArray[1]) +
                  MILLISECONDS_PER_DAY * dayDifference
              ).toLocaleDateString("en-us");
              foundDocument.installer = "";
              foundDocument.statusCode = 4;
              foundDocument.extraCellId = extraCellId;
              foundDocument.extraCellText = extraCellCustomText;
              break;
            case "Unassigned_Wed":
              foundDocument.date_scheduled = weekDatesArray[2];
              foundDocument.date_estimatedCompletion = new Date(
                Date.parse(weekDatesArray[2]) +
                  MILLISECONDS_PER_DAY * dayDifference
              ).toLocaleDateString("en-us");
              foundDocument.installer = "";
              foundDocument.statusCode = 4;
              foundDocument.extraCellId = extraCellId;
              foundDocument.extraCellText = extraCellCustomText;
              break;
            case "Unassigned_Thr":
              foundDocument.date_scheduled = weekDatesArray[3];
              foundDocument.date_estimatedCompletion = new Date(
                Date.parse(weekDatesArray[3]) +
                  MILLISECONDS_PER_DAY * dayDifference
              ).toLocaleDateString("en-us");
              foundDocument.installer = "";
              foundDocument.statusCode = 4;
              foundDocument.extraCellId = extraCellId;
              foundDocument.extraCellText = extraCellCustomText;
              break;
            case "Unassigned_Fri":
              foundDocument.date_scheduled = weekDatesArray[4];
              foundDocument.date_estimatedCompletion = new Date(
                Date.parse(weekDatesArray[4]) +
                  MILLISECONDS_PER_DAY * dayDifference
              ).toLocaleDateString("en-us");
              foundDocument.installer = "";
              foundDocument.statusCode = 4;
              foundDocument.extraCellId = extraCellId;
              foundDocument.extraCellText = extraCellCustomText;
              break;
            case "Unassigned_Sat":
              foundDocument.date_scheduled = weekDatesArray[5];
              foundDocument.date_estimatedCompletion = new Date(
                Date.parse(weekDatesArray[5]) +
                  MILLISECONDS_PER_DAY * dayDifference
              ).toLocaleDateString("en-us");
              foundDocument.installer = "";
              foundDocument.statusCode = 4;
              foundDocument.extraCellId = extraCellId;
              foundDocument.extraCellText = extraCellCustomText;
              break;
            case "Unassigned_Sun":
              foundDocument.date_scheduled = weekDatesArray[6];
              foundDocument.date_estimatedCompletion = new Date(
                Date.parse(weekDatesArray[6]) +
                  MILLISECONDS_PER_DAY * dayDifference
              ).toLocaleDateString("en-us");
              foundDocument.installer = "";
              foundDocument.statusCode = 4;
              foundDocument.extraCellId = extraCellId;
              foundDocument.extraCellText = extraCellCustomText;
              break;
            case "Unassigned_RTS":
              // additional handling if this was an extraCell married to a job
              if (
                foundDocument.extraCellText &&
                foundDocument.extraCellText !== ""
              ) {
                // extraCell was already deleted when job was assigned, so now re-create it
                const originalScheduledDate = foundDocument.date_scheduled;
                const surfaceToUse =
                  foundDocument.surface === "Carpet"
                    ? "Carpet"
                    : "Hard Surface";
                // Use TanStack directly (Realm EOL Jan 13, 2026)
                tsCreateExtraCellAttribute(
                  originalScheduledDate,
                  surfaceToUse,
                  foundDocument.extraCellText
                );
              }
              foundDocument.date_scheduled = "";
              foundDocument.date_estimatedCompletion = "";
              foundDocument.installer = "";
              foundDocument.statusCode = 3;
              foundDocument.extraCellId = null;
              foundDocument.extraCellText = null;
              break;
            default:
              console.log(
                "JobInitModal useEffect[] 'Unassigned' switch ERROR; no case matched..."
              );
              break;
          } // switch

          console.log(
            "JobInitModal useEffect[] 'Unassigned' switch, date_scheduled: ",
            foundDocument.date_scheduled,
            ", date_estimatedCompletion: ",
            foundDocument.date_estimatedCompletion,
            ", statusCode: ",
            foundDocument.statusCode
          );
          //
        } else {
          // (dropObject.id.slice(0, 10) === "Unassigned")
          // dropped onto crew cell, assigning the job
          foundDocument.date_scheduled = weekDatesArray[droppedColumnIndex];
          foundDocument.date_estimatedCompletion = new Date(
            Date.parse(weekDatesArray[droppedColumnIndex]) +
              MILLISECONDS_PER_DAY * dayDifference
          ).toLocaleDateString("en-us");
          foundDocument.installer = droppedPayrollNo;
          foundDocument.statusCode = 4;
          console.log(
            "JobInitModal useEffect[] 'Assigned' switch, payrollNo: ",
            droppedPayrollNo,
            ", colIndex: ",
            droppedColumnIndex,
            ", date_scheduled: ",
            foundDocument.date_scheduled,
            ", date_estimatedCompletion: ",
            foundDocument.date_estimatedCompletion,
            ", statusCode: ",
            foundDocument.statusCode
          );
        }
        //
      } else {
        // (!nonCompliantFlag)
        // non-compliant
        console.log("JobInitModal useEffect[] ERROR non-compliant...");
        setOpenJobInitModal({ show: false });
        window.alert(
          "Compliance Hold: Please contact your Regional Manager for assistance."
        );
      }

      // add to local state
      setJobDocument(foundDocument);
      setCurrentEstComplete(foundDocument?.date_estimatedCompletion);
      setCurrentArrivalTime(foundDocument?.arrival_time_text);
      //
    } else {
      // (foundIndex !== -1)
      // this should never happen, but just in case...
      console.log(
        "JobInitModal useEffect[] ERROR couldn't find project, dragObject: ",
        dragObject
      );
      setOpenJobInitModal({ show: false });
      window.alert("Drag failed!");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // - - - - - - - - - -

  return (
    <Modal onClose={() => setOpenJobInitModal({ show: false })}>
      <Modal.Header>
        Modifying Job#{" "}
        <span className="font-bold">{jobDocument.lowes_projectNo}</span>
      </Modal.Header>
      <Modal.Content>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Invoice#:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.invoiceNo}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Classification:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.classification}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Customer:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.customer_lastName}, {jobDocument.customer_firstName}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">StoreNo:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.lowes_storeNo}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Installer:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.installer}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Scheduled:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.date_scheduled}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Est Complete</div>
            <div className="flex-1 basis-2/4 font-bold">
              <DatePicker
                selected={currentEstComplete}
                onChange={(date) => onEstCompleteChange(date)}
                className=""
                customInput={
                  <EstCompleteInput
                    className={`w-28 h-6 bg-crew-surface-hard border-l border-l-black/[.20]`}
                  />
                }
                dateFormat="M/d/YYYY"
              />
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Arrival</div>
            <div className="flex-1 basis-2/4 font-bold">
              <select
                className="bg-yellow-30 text-black text-sm font-medium hover:opacity-75 cursor-pointer"
                value={currentArrivalTime}
                onChange={(e) => onArrivalChange(e.target.value)}
              >
                <option value="8 AM - 11 AM">8 AM - 11 AM</option>
                <option value="11 AM - 2 PM">11 AM - 2 PM</option>
                <option value="2 PM - 5 PM">2 PM - 5 PM</option>
                <option value={currentArrivalTime}>{currentArrivalTime}</option>
              </select>
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className="h-8 flex gap-4">
          <Button className="uppercase w-31" onClick={() => onSave()}>
            Save
          </Button>
          <Button
            color="light"
            className="uppercase w-31"
            onClick={() => setOpenJobInitModal({ show: false })}
          >
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default JobInitModal;
